import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../store';
import qache_splash_logo from '../assets/other/qache_splash.png';
import { useWeb3Auth } from '../auth/Web3AuthContext';
import { Button, PreloadedImage } from '../components';
import { theme } from '../constants';
import { addUserToFirestore } from '../firebase';
import { hooks } from '../hooks';
import { setDisplayName, setUser } from '../store/slices/userSlice';
import { setAuthenticated } from '../store/slices/authSlice';
import { text } from '../text';
import { usePWAInstall } from '../contexts/PWAInstallContext';
import {
  getWithdrawInProgress,
  readPendingGuardarianWithdraw,
} from '../services/WithdrawService';

export const SignIn: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;
  const navigate = hooks.useAppNavigate();

  const { isLoggedIn, login, getUserInfo, getAddress, isInitialized, isLoading, isFirebaseReady, isRestoringSession } = useWeb3Auth();
  const isAuthenticated = useSelector((state: RootState) => state.authSlice.isAuthenticated);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(window.matchMedia('(display-mode: standalone)').matches);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasAttemptedAutoLogin, setHasAttemptedAutoLogin] = useState(false);
  const [hasAttemptedCallbackRecovery, setHasAttemptedCallbackRecovery] = useState(false);

  const { canInstallPWA, triggerInstallPrompt } = usePWAInstall();

  // In your SignIn component
  useEffect(() => {
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = qache_splash_logo; // This will use the imported image path
    preloadLink.type = 'image/png';
    document.head.appendChild(preloadLink);

    return () => {
      // Clean up when component unmounts
      document.head.removeChild(preloadLink);
    };
  }, []);

  useEffect(() => {
    if (!isInitialized || !isLoggedIn || !isFirebaseReady || hasAttemptedAutoLogin) {
      return;
    }

    const autoLogin = async () => {
      setHasAttemptedAutoLogin(true);
      if (!isProcessing && !isLoading) {
        try {
          await processLoginSuccess();
        } catch (error) {
          console.error("Auto-login error:", error);
        }
      }
    };

    autoLogin();
  }, [isInitialized, isLoggedIn, isFirebaseReady]);

  // Returning to the app already authenticated — e.g. closing the out-of-scope
  // Guardarian checkout drops the installed PWA back on its start_url '/', which
  // renders this screen. The persisted auth flag survives that round trip even
  // though the live Web3Auth provider does not, so forward into the app instead
  // of stranding the user on SignIn while the provider rehydrates in the
  // background. Skipped while a fresh login is mid-flight so we don't pre-empt
  // its transition; logout clears isAuthenticated, so signed-out users stay here.
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || isProcessing || isTransitioning) {
      return;
    }
    const fromLocation = (location.state as any)?.from;
    const fromPath = fromLocation?.pathname;
    const resumePath = fromPath && fromPath !== '/' && fromPath !== '/SignIn'
      ? `${fromPath}${fromLocation.search || ''}`
      : '/TabNavigator';
    navigate(resumePath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, isAuthenticated, isProcessing, isTransitioning]);

  useEffect(() => {
    if (!isInitialized || isLoggedIn || isProcessing || isLoading || hasAttemptedCallbackRecovery) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const hasGuardarianCallbackQuery = Boolean(params.get('guardarian') || params.get('tid'));
    const fromPath = (location.state as any)?.from?.pathname || '';
    const fromSearch = (location.state as any)?.from?.search || '';
    const fromGuardarianCallback =
      fromPath === '/withdraw' &&
      (fromSearch.includes('guardarian=') || fromSearch.includes('tid='));

    const hasWithdrawRecoveryState = getWithdrawInProgress() || Boolean(readPendingGuardarianWithdraw()?.txId);
    const shouldTryCallbackRecovery = hasGuardarianCallbackQuery || fromGuardarianCallback || hasWithdrawRecoveryState;

    if (!shouldTryCallbackRecovery) {
      return;
    }

    const recoverSession = async () => {
      setHasAttemptedCallbackRecovery(true);
      setIsProcessing(true);
      try {
        await login();
        await processLoginSuccess();
      } catch (recoveryError) {
        console.warn('Callback recovery login failed, waiting for manual sign in:', recoveryError);
        setIsProcessing(false);
      }
    };

    recoverSession();
  }, [isInitialized, isLoggedIn, isProcessing, isLoading, hasAttemptedCallbackRecovery, location.state]);

  // Only show errors if we're past initialization
  const displayError = error && isInitialized && hasAttemptedAutoLogin;

  // Drive the sign-in buttons' loading state from the first moment of activity:
  // - while Web3Auth is still initialising / restoring a cached session, and
  // - the instant a login is in flight (isProcessing is set on click, before login()).
  // This removes the "dead" window where the buttons looked idle while connect()
  // and the Firebase token exchange were running.
  const isAuthBusy = isProcessing || isLoading || !isInitialized || isRestoringSession;

  // Handle initial page setup
  useEffect(() => {
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: 'smooth' });
    }, 10);

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    metaThemeColor?.setAttribute('content', '#ffffff');
  }, [pathname]);

  useEffect(() => {
    const updateInstallState = () => {
      const installed = window.matchMedia('(display-mode: standalone)').matches;
      setIsAppInstalled(installed);
    };
    updateInstallState(); // Initial check
    window.matchMedia('(display-mode: standalone)').addEventListener('change', updateInstallState);
    return () => window.matchMedia('(display-mode: standalone)').removeEventListener('change', updateInstallState);
  }, []);

  // Handle initial page setup
  useEffect(() => {
    const setup = async () => {
      try {
        // Only proceed with auto-login if already logged in and firebase session is ready
        if (isLoggedIn && isFirebaseReady && !isProcessing) {
          await processLoginSuccess();
        }
      } catch (error) {
        console.error("Setup error:", error);
        // setError("Failed to initialize. Please try again.");
      }
    };

    setup();
  }, [isLoggedIn, isFirebaseReady]);

  const processLoginSuccess = async () => {
    if (!isInitialized) {
      throw new Error("Web3Auth not initialized");
    }

    setIsProcessing(true);
    setError(null);

    try {
      const [userInfo, address] = await Promise.all([
        getUserInfo(),
        getAddress()
      ]);

      // v10 renamed verifierId to userId; for Google OAuth both resolve to the email
      const verifierId = (userInfo as any)?.verifierId || (userInfo as any)?.userId || userInfo?.email;
      if (!userInfo?.email || !verifierId || !userInfo?.name || !address) {
        console.error("getUserInfo returned:", userInfo, "address:", address);
        throw new Error("Missing required user information");
      }

      await addUserToFirestore(
        verifierId,
        userInfo.email,
        userInfo.name,
        address
      );

      dispatch(setAuthenticated(true));
      dispatch(setUser({
        verifierId: verifierId,
        email: userInfo.email,
        name: userInfo.name,
        address: address
      }));

      dispatch(setDisplayName(userInfo.name));

      setIsTransitioning(true);
      setTimeout(() => {
        const fromLocation = (location.state as any)?.from;
        const resumePath = fromLocation?.pathname
          ? `${fromLocation.pathname}${fromLocation.search || ''}`
          : '/TabNavigator';
        // Replace (not push) so the SignIn entry — which carries a stale
        // `from` location from the ProtectedRoute redirect — is removed from
        // history. Otherwise pressing back from the resumed page returns to
        // SignIn, which auto-logs in and re-navigates forward, creating a loop.
        navigate(resumePath, { replace: true });
      }, 1000);

    } catch (error) {
      console.error("Login processing error:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLogin = async () => {
    if (isProcessing || isLoading || !isInitialized) {
      return;
    }

    setError(null);
    // Show the spinner immediately on tap — before the (potentially slow)
    // Web3Auth connect() + /firebase-token exchange that login() performs.
    setIsProcessing(true);

    try {
      await login();
      await processLoginSuccess();
    } catch (error) {
      console.error("Login error:", error);
      setError("Failed to sign in. Please try again.");
      setIsProcessing(false);
    }
  };

  const handlePWAInstallClick = async () => {
    if (!canInstallPWA) {
      setShowInstallInstructions(true);
      return;
    }
    const { outcome } = await triggerInstallPrompt();
    if (outcome === 'accepted') {
      // setIsAppInstalled(true); // Context and appinstalled event should handle this
    } else if (outcome === 'dismissed') {
      // Optionally show manual instructions if user dismissed and prompt not available again
      // setShowInstallInstructions(true);
    }
  };

  const renderInstallInstructions = (): JSX.Element => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOS) {
      return (
        <div>
          <text.T14>To install on iOS:</text.T14>
          <text.T12>1. Tap Share in Safari</text.T12>
          <text.T12>2. Choose "Add to Home Screen"</text.T12>
          <text.T12>3. Tap "Add"</text.T12>
        </div>
      );
    } else if (isAndroid) {
      return (
        <div>
          <text.T14>To install on Android:</text.T14>
          <text.T12>1. Tap menu in Chrome</text.T12>
          <text.T12>2. Choose "Add to Home screen"</text.T12>
          <text.T12>3. Tap "Add"</text.T12>
        </div>
      );
    } else {
      return (
        <div>
          <text.T14>To install:</text.T14>
          <text.T12>Look for install icon in browser or menu</text.T12>
        </div>
      );
    }
  };

  const styles = {
    gradientBackground: {
      width: '100%',
      height: '100vh',
      color: '#fff',
      background: 'linear-gradient(-45deg, #E1EEC3, #F2C94C, #F2994A, #F2C94C)',
      backgroundSize: '400% 400%',
      animation: 'Gradient 15s ease infinite',
      display: 'flex',
      flexDirection: 'column' as const,
      position: 'relative' as const,
    },
    contentWrapper: {
      display: 'flex',
      flexDirection: 'column' as const,
      height: '100%',
      position: 'relative' as const,
    },
    logoContainer: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center', // Keep centered
      padding: '20px',
      minHeight: '45vh',
    },
    logo: {
      maxWidth: '50%',
      maxHeight: '30vh',
    },
    welcomeContainer: {
      backgroundColor: theme.colors.white,
      borderTopLeftRadius: '52px',
      borderTopRightRadius: '52px',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'flex-start',
      height: isAppInstalled ? 'auto' : '35vh',
      minHeight: 'min-content',
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${isAppInstalled ? '20px' : '40px'})`,
      transition: 'all 350ms ease-in-out',
    },
    buttonWrapper: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '15px',
      marginTop: '20px',
      marginBottom: isAppInstalled ? '0' : '20px',
    },
    transitionOverlay: {
      position: 'absolute' as const,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.colors.white,
      height: isTransitioning ? '100vh' : '0vh',
      transition: 'height 350ms ease-in-out',
      zIndex: 1,
    },
  };

  const renderContent = (): JSX.Element => {
    return (
      <div style={styles.gradientBackground}>
        <div style={styles.transitionOverlay} />

        <div style={styles.contentWrapper}>
          <div style={styles.logoContainer}>
            <PreloadedImage
              src={qache_splash_logo}
              alt="Qache App Logo"
              priority={true}
              style={{
                ...styles.logo,
                transition: 'opacity 300ms ease-in-out',
                opacity: isTransitioning ? 0 : 1,
              }}
            />
          </div>
          <div style={styles.welcomeContainer}>
            <text.H3
              style={{
                marginBottom: '15px',
                transition: 'opacity 300ms ease-in-out',
                opacity: isTransitioning ? 0 : 1,
              }}
            >
              Welcome
            </text.H3>

            {displayError && (
              <text.T14 style={{ color: 'red', marginBottom: '10px' }}>
                {error}
              </text.T14>
            )}

            {!showInstallInstructions ? (
              <text.T14
                style={{
                  marginBottom: '20px',
                  transition: 'opacity 300ms ease-in-out',
                  opacity: isTransitioning ? 0 : 1,
                }}
              >
                Your weekly chance to Double your Ethereum.
              </text.T14>
            ) : (
              renderInstallInstructions()
            )}
            <div style={styles.buttonWrapper}>
              <Button
                title='Sign Up'
                onClick={handleLogin}
                isLoading={isAuthBusy}
                disabled={isAuthBusy}
              />
              <Button
                title='Sign In'
                onClick={handleLogin}
                isLoading={isAuthBusy}
                disabled={isAuthBusy}
              />
              {!isAppInstalled && canInstallPWA && (
                <Button
                  title='Install App'
                  onClick={handlePWAInstallClick}
                  disabled={isProcessing || isLoading}
                  style={{
                    opacity: canInstallPWA ? 1 : 0,
                    transition: 'opacity 0.5s ease-in-out',
                  }}
                />
              )}
              {!isAppInstalled && !canInstallPWA && showInstallInstructions && (
                <Button
                  title='Installation Help'
                  onClick={() => setShowInstallInstructions(true)}
                  disabled={isProcessing || isLoading}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id='screen' style={{ height: '100vh', overflow: 'hidden' }}>
      {renderContent()}
      <style>
        {`
          @keyframes Gradient {
            0% { background-position: 0% 50% }
            50% { background-position: 100% 50% }
            100% { background-position: 0% 50% }
          }
          @supports (padding: max(0px)) {
            .welcomeContainer {
              padding-bottom: max(${isAppInstalled ? '20px' : '40px'}, env(safe-area-inset-bottom));
            }
          }
        `}
      </style>
    </div>
  );
};  